import { UserPermission } from '@repo/permissions'

export const CORE_CSV_UPLOAD_TYPES = [
  'auto-distro',
  'auto-inject',
  'auto-inject-delivery-dates',
  'auto-inject-pick-and-ship',
  'auto-inject-corner-orders',
  'auto-inject-rma',
  'edi-poconf-control',
  'erp-material-substitution',
  'on-hand-inventory-partial',
  'on-hand-inventory-uom',
  'on-hand-inventory-duns',
  'limit-quantity',
  'limit-quantity-exemptions',
  'material-specification-conversion',
  'material-upc-override',
  'merchandise',
  'nod-add-position',
  'o2c-payment-terms',
  'route-stop-sequence',
  'suspend-order-submission',
  'tax-rate',
] as const

export type CoreCsvUploadType = (typeof CORE_CSV_UPLOAD_TYPES)[number]

export const CORE_CSV_UPLOAD_TYPE_LABELS: Record<CoreCsvUploadType, string> = {
  'auto-distro': 'Auto Distro',
  'auto-inject': 'Auto Inject',
  'auto-inject-delivery-dates': 'Auto Inject (Delivery Dates)',
  'auto-inject-pick-and-ship': 'Auto Inject Pick and Ship',
  'auto-inject-corner-orders': 'Auto Inject Corner Orders',
  'auto-inject-rma': 'Auto Inject RMA',
  'edi-poconf-control': 'EDI POConf Control',
  'erp-material-substitution': 'ERP Material Substitution',
  'limit-quantity': 'Limit Quantity',
  'limit-quantity-exemptions': 'Limit Quantity Exemptions',
  'material-specification-conversion': 'Material Specification Conversion',
  'material-upc-override': 'Material UPC Override',
  merchandise: 'Merchandise',
  'nod-add-position': 'NOD Add Position',
  'o2c-payment-terms': 'O2C Payment Terms',
  'on-hand-inventory-duns': 'On-hand Inventory DUNS',
  'on-hand-inventory-partial': 'On-hand Inventory Partial',
  'on-hand-inventory-uom': 'On-hand Inventory UOM',
  'route-stop-sequence': 'Route Stop Sequence',
  'suspend-order-submission': 'Suspend Order Submission',
  'tax-rate': 'Tax Rate',
}

export const CORE_UPLOAD_TYPE_PERMISSIONS: Record<CoreCsvUploadType, UserPermission> = {
  'auto-distro': 'upload_autoDistro',
  'auto-inject': 'upload_autoInject',
  'auto-inject-delivery-dates': 'upload_autoInjectDeliveryDates',
  // The Nova pair reuses the mainline permissions rather than minting new names. The capability is
  // identical, "may upload auto-inject data"; only the folder Fuze Core reads it from differs, and
  // a permission has no business knowing that.
  'auto-inject-pick-and-ship': 'upload_autoInject',
  'auto-inject-corner-orders': 'upload_autoInjectDeliveryDates',
  'auto-inject-rma': 'upload_autoInjectRMA',
  'edi-poconf-control': 'upload_ediPOConfControl',
  'erp-material-substitution': 'upload_erpSkuSubstitution',
  'limit-quantity': 'upload_limitQuantity',
  'limit-quantity-exemptions': 'upload_limitQuantityExemptions',
  'material-specification-conversion': 'upload_materialSpecificationConversion',
  'material-upc-override': 'upload_skuUpcOverride',
  merchandise: 'upload_merchandise',
  'nod-add-position': 'upload_nodAddPosition',
  'o2c-payment-terms': 'orders_payment_review_release',
  'on-hand-inventory-duns': 'upload_onHandInventoryDuns',
  'on-hand-inventory-partial': 'upload_onHandInventoryPartial',
  'on-hand-inventory-uom': 'upload_onHandInventoryUOM',
  'route-stop-sequence': 'upload_routeStopSequence',
  'suspend-order-submission': 'upload_suspendOrderSubmission',
  'tax-rate': 'upload_taxRate',
} as const

export const CORE_UPLOAD_TYPE_S3_PATH: Record<CoreCsvUploadType, string> = {
  // The mainline pair, both landing in the folder Fuze Core reads today.
  'auto-inject': 'autoInject_inbound_orders',
  'auto-inject-delivery-dates': 'autoInject_inbound_orders',
  // The Nova pair. Split out of the shared folder for the Payment Term CR, because Fuze Core
  // ingests each folder separately there and no longer reads the shared one. Each line keeps the
  // pair its own Core is configured for, so neither inherits the other's folders at the fold.
  'auto-inject-pick-and-ship': 'autoInject_pick_and_ship_orders',
  'auto-inject-corner-orders': 'autoInject_corner_orders',
  'auto-inject-rma': 'autorma_inbound_orders',
  'auto-distro': 'sales_order_cfg_autodistro',
  'edi-poconf-control': 'poconf_edi',
  'nod-add-position': 'nod_position',
  // Folder is provisional: ITC warned on 2026-07-27 the name may still change.
  'o2c-payment-terms': 'o2c_uploads',
  'material-specification-conversion': 'material_specification_conversion',
  merchandise: 'material_merchandise',
  'material-upc-override': 'sku_upc',
  'erp-material-substitution': 'sales_order_sku_substitution',
  'on-hand-inventory-duns': 'invupl_duns',
  'on-hand-inventory-partial': 'invupl_partial',
  'on-hand-inventory-uom': 'invupl_uom',
  'route-stop-sequence': 'sales_order_route_stop_sequence',
  'limit-quantity': 'sales_order_limit_qty',
  'limit-quantity-exemptions': 'sales_order_limit_qty_ext',
  'suspend-order-submission': 'sales_order_business_parameters',
  'tax-rate': 'tax_rate',
} as const
